import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Calculator, FileText, Download, RefreshCw, 
  DollarSign, Users, Calendar, Check, AlertTriangle,
  Import, ClipboardList, Settings, Eye, Edit2
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

export default function PayrollCalculator() {
  const { api, isAdmin, user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [payPeriods, setPayPeriods] = useState([]);
  const [paystubs, setPaystubs] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('workflow');

  // Workflow states
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedPayPeriod, setSelectedPayPeriod] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [currentDraft, setCurrentDraft] = useState(null);
  const [workflowStep, setWorkflowStep] = useState(1); // 1: Select, 2: Import, 3: Review, 4: Generate

  // Dialog states
  const [showCalculation, setShowCalculation] = useState(false);
  const [showEmployeeSettings, setShowEmployeeSettings] = useState(false);
  const [settingsEmployee, setSettingsEmployee] = useState(null);
  const [settingsForm, setSettingsForm] = useState({
    hourly_rate: 0,
    overtime_rate: 0,
    payroll_active: true,
    employee_number: '',
    vacation_pay_percent: 4.0
  });

  useEffect(() => {
    if (!isAdmin) return;
    fetchData();
  }, [isAdmin]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [employeesRes, periodsRes, paystubsRes, draftsRes] = await Promise.all([
        api.get('/payroll/employees'),
        api.get('/timesheets/pay-periods'),
        api.get('/payroll/paystubs'),
        api.get('/payroll/drafts')
      ]);
      setEmployees(employeesRes.data);
      setPayPeriods(periodsRes.data);
      setPaystubs(paystubsRes.data);
      setDrafts(draftsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Check for approved timesheet
  const checkTimesheetForImport = async () => {
    if (!selectedEmployee || !selectedPayPeriod) {
      toast.error('Please select an employee and pay period');
      return;
    }

    try {
      const response = await api.get(`/payroll/import-timesheet/${selectedPayPeriod}/${selectedEmployee}`);
      setImportResult(response.data);
      setWorkflowStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check timesheet');
    }
  };

  // Import timesheet to draft
  const importTimesheet = async () => {
    try {
      const response = await api.post('/payroll/import-timesheet', {
        employee_id: selectedEmployee,
        pay_period_id: selectedPayPeriod
      });
      setCurrentDraft(response.data);
      setWorkflowStep(3);
      toast.success('Timesheet imported to payroll draft');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import timesheet');
    }
  };

  // Update draft
  const updateDraft = async (updates) => {
    if (!currentDraft) return;
    try {
      const response = await api.put(`/payroll/drafts/${currentDraft.id}`, updates);
      setCurrentDraft(response.data);
      toast.success('Draft updated');
    } catch (error) {
      toast.error('Failed to update draft');
    }
  };

  // Calculate payroll
  const calculatePayroll = async () => {
    if (!currentDraft) return;
    try {
      const response = await api.post(`/payroll/drafts/${currentDraft.id}/calculate`);
      setCurrentDraft(response.data);
      setShowCalculation(true);
      toast.success('Payroll calculated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to calculate');
    }
  };

  // Generate paystub
  const generatePaystub = async () => {
    if (!currentDraft || !currentDraft.calculation) return;
    const payDate = format(new Date(), 'yyyy-MM-dd');
    try {
      const response = await api.post(`/payroll/drafts/${currentDraft.id}/generate-paystub?pay_date=${payDate}`);
      toast.success('Paystub generated!');
      setShowCalculation(false);
      setWorkflowStep(4);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate paystub');
    }
  };

  // Finalize and generate PDF
  const finalizePaystub = async (paystubId) => {
    try {
      await api.post(`/payroll/paystubs/${paystubId}/finalize`);
      toast.success('Paystub finalized and PDF generated');
      fetchData();
    } catch (error) {
      toast.error('Failed to finalize paystub');
    }
  };

  // Download PDF
  const downloadPDF = async (paystubId) => {
    try {
      const response = await api.get(`/payroll/paystubs/${paystubId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `paystub_${paystubId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  // Employee settings
  const openEmployeeSettings = async (employee) => {
    try {
      const response = await api.get(`/payroll/employee-settings/${employee.id}`);
      setSettingsEmployee(employee);
      setSettingsForm({
        hourly_rate: response.data.hourly_rate || 0,
        overtime_rate: response.data.overtime_rate || 0,
        payroll_active: response.data.payroll_active !== false,
        employee_number: response.data.employee_number || '',
        vacation_pay_percent: response.data.vacation_pay_percent || 4.0
      });
      setShowEmployeeSettings(true);
    } catch (error) {
      toast.error('Failed to load settings');
    }
  };

  const saveEmployeeSettings = async () => {
    if (!settingsEmployee) return;
    try {
      await api.put(`/payroll/employee-settings/${settingsEmployee.id}`, settingsForm);
      toast.success('Settings saved');
      setShowEmployeeSettings(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  // Reset workflow
  const resetWorkflow = () => {
    setWorkflowStep(1);
    setSelectedEmployee('');
    setSelectedPayPeriod('');
    setImportResult(null);
    setCurrentDraft(null);
  };

  // Load existing draft
  const loadDraft = (draft) => {
    setSelectedEmployee(draft.employee_id);
    setSelectedPayPeriod(draft.pay_period_id);
    setCurrentDraft(draft);
    setWorkflowStep(3);
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-slate-100 text-slate-700',
      calculated: 'bg-blue-100 text-blue-700',
      finalized: 'bg-emerald-100 text-emerald-700'
    };
    return <Badge className={styles[status] || styles.draft}>{status}</Badge>;
  };

  const getPayrollStatusBadge = (status) => {
    const styles = {
      'not_processed': 'bg-slate-100 text-slate-600',
      'imported_to_draft': 'bg-amber-100 text-amber-700',
      'paystub_generated': 'bg-emerald-100 text-emerald-700'
    };
    const labels = {
      'not_processed': 'Not Processed',
      'imported_to_draft': 'In Payroll Draft',
      'paystub_generated': 'Paystub Generated'
    };
    return <Badge className={styles[status] || styles['not_processed']}>{labels[status] || status}</Badge>;
  };

  // Non-admin view
  if (!isAdmin) {
    return (
      <div className="space-y-6" data-testid="payroll-employee-view">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">My Paystubs</h1>
          <p className="text-slate-500 mt-1">View your pay statements</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-teal-600" />
              Paystub History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : paystubs.filter(s => s.status === 'finalized').length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 mx-auto text-slate-300" />
                <p className="mt-4 text-slate-500">No paystubs available yet</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pay Period</TableHead>
                    <TableHead>Pay Date</TableHead>
                    <TableHead>Gross</TableHead>
                    <TableHead>Deductions</TableHead>
                    <TableHead>Net Pay</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paystubs.filter(s => s.status === 'finalized').map((stub) => (
                    <TableRow key={stub.id}>
                      <TableCell>{stub.pay_period_start} to {stub.pay_period_end}</TableCell>
                      <TableCell>{stub.pay_date}</TableCell>
                      <TableCell>${stub.gross_earnings?.toFixed(2)}</TableCell>
                      <TableCell>${stub.total_deductions?.toFixed(2)}</TableCell>
                      <TableCell className="font-bold text-emerald-600">${stub.net_pay?.toFixed(2)}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" onClick={() => downloadPDF(stub.id)}>
                          <Download className="h-4 w-4 mr-1" /> PDF
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin view
  return (
    <div className="space-y-6" data-testid="payroll-calculator-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Payroll Workflow</h1>
          <p className="text-slate-500 mt-1">Import timesheets and generate paystubs for employees</p>
        </div>
      </div>

      <Alert className="bg-blue-50 border-blue-200">
        <AlertTriangle className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-700">
          <strong>Workflow:</strong> Select Employee → Select Pay Period → Import Approved Timesheet → Review & Adjust → Calculate Deductions → Generate Paystub PDF
        </AlertDescription>
      </Alert>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 w-full max-w-[600px]">
          <TabsTrigger value="workflow" className="flex items-center gap-2">
            <Import className="h-4 w-4" />
            Workflow
          </TabsTrigger>
          <TabsTrigger value="drafts" className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4" />
            Drafts
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Paystubs
          </TabsTrigger>
          <TabsTrigger value="employees" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Settings
          </TabsTrigger>
        </TabsList>

        {/* Workflow Tab */}
        <TabsContent value="workflow" className="space-y-6">
          {/* Step Indicator */}
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            {[
              { num: 1, label: 'Select' },
              { num: 2, label: 'Check' },
              { num: 3, label: 'Review' },
              { num: 4, label: 'Complete' }
            ].map((step, idx) => (
              <div key={step.num} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold
                  ${workflowStep >= step.num ? 'bg-teal-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
                  {workflowStep > step.num ? <Check className="h-5 w-5" /> : step.num}
                </div>
                <span className={`ml-2 text-sm ${workflowStep >= step.num ? 'text-teal-600 font-medium' : 'text-slate-500'}`}>
                  {step.label}
                </span>
                {idx < 3 && <div className={`w-16 h-1 mx-4 ${workflowStep > step.num ? 'bg-teal-600' : 'bg-slate-200'}`} />}
              </div>
            ))}
          </div>

          {/* Step 1: Select Employee & Pay Period */}
          {workflowStep === 1 && (
            <Card>
              <CardHeader>
                <CardTitle>Step 1: Select Employee & Pay Period</CardTitle>
                <CardDescription>Choose the employee and pay period to process payroll</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Employee *</Label>
                    <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                      <SelectTrigger data-testid="select-employee">
                        <Users className="h-4 w-4 mr-2" />
                        <SelectValue placeholder="Select employee" />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map(emp => (
                          <SelectItem key={emp.id} value={emp.id}>
                            {emp.name} {emp.hourly_rate > 0 ? `($${emp.hourly_rate}/hr)` : '(No rate set)'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedEmployee && employees.find(e => e.id === selectedEmployee)?.hourly_rate <= 0 && (
                      <p className="text-sm text-amber-600">
                        <AlertTriangle className="h-4 w-4 inline mr-1" />
                        Hourly rate not configured. Set it in Settings tab.
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Pay Period *</Label>
                    <Select value={selectedPayPeriod} onValueChange={setSelectedPayPeriod}>
                      <SelectTrigger data-testid="select-pay-period">
                        <Calendar className="h-4 w-4 mr-2" />
                        <SelectValue placeholder="Select pay period" />
                      </SelectTrigger>
                      <SelectContent>
                        {payPeriods.map(period => (
                          <SelectItem key={period.id} value={period.id}>
                            {period.name} ({period.start_date} to {period.end_date})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button 
                  className="bg-teal-600 hover:bg-teal-700"
                  onClick={checkTimesheetForImport}
                  disabled={!selectedEmployee || !selectedPayPeriod}
                  data-testid="check-timesheet-btn"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Check for Approved Timesheet
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Import Result */}
          {workflowStep === 2 && importResult && (
            <Card>
              <CardHeader>
                <CardTitle>Step 2: Timesheet Import Status</CardTitle>
                <CardDescription>Review timesheet availability for {importResult.employee_name}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-sm text-slate-500">Employee</p>
                    <p className="font-medium">{importResult.employee_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Pay Period</p>
                    <p className="font-medium">{importResult.pay_period?.start_date} to {importResult.pay_period?.end_date}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Hourly Rate</p>
                    <p className="font-medium">${importResult.hourly_rate?.toFixed(2)}/hr</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Approved Timesheet</p>
                    <Badge className={importResult.timesheet_found ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                      {importResult.timesheet_found ? 'Found' : 'Not Found'}
                    </Badge>
                  </div>
                </div>

                {importResult.timesheet && (
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <p className="font-medium text-emerald-800 mb-2">Approved Timesheet Details</p>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">Total Hours</p>
                        <p className="font-bold text-lg">{importResult.timesheet.total_hours?.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Days Worked</p>
                        <p className="font-bold text-lg">{importResult.timesheet.entries?.length || 0}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Status</p>
                        <Badge className="bg-emerald-500 text-white">{importResult.timesheet.status}</Badge>
                      </div>
                    </div>
                  </div>
                )}

                {!importResult.timesheet_found && (
                  <Alert className="border-amber-200 bg-amber-50">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-700">
                      No approved timesheet found for this pay period. The employee must submit and have their timesheet approved first.
                    </AlertDescription>
                  </Alert>
                )}

                {importResult.existing_paystub && (
                  <Alert className="border-amber-200 bg-amber-50">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-700">
                      A paystub already exists for this pay period. Creating another may result in duplicate payments.
                    </AlertDescription>
                  </Alert>
                )}

                {importResult.hourly_rate <= 0 && (
                  <Alert className="border-red-200 bg-red-50">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-700">
                      Hourly rate is not configured for this employee. Please set it in the Settings tab first.
                    </AlertDescription>
                  </Alert>
                )}

                <div className="flex gap-3">
                  <Button variant="outline" onClick={resetWorkflow}>
                    Back
                  </Button>
                  <Button 
                    className="bg-teal-600 hover:bg-teal-700"
                    onClick={importTimesheet}
                    disabled={!importResult.timesheet_found || importResult.hourly_rate <= 0}
                    data-testid="import-timesheet-btn"
                  >
                    <Import className="h-4 w-4 mr-2" />
                    Import to Payroll Draft
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Review & Edit Draft */}
          {workflowStep === 3 && currentDraft && (
            <Card>
              <CardHeader>
                <CardTitle>Step 3: Review Payroll Draft</CardTitle>
                <CardDescription>Review and adjust hours/earnings before calculating deductions</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Employee Info */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Employee</p>
                      <p className="font-medium">{currentDraft.employee_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Pay Period</p>
                      <p className="font-medium">{currentDraft.pay_period_start} to {currentDraft.pay_period_end}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Imported Hours</p>
                      <p className="font-bold text-lg">{currentDraft.imported_hours?.toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                {/* Timesheet Breakdown */}
                {currentDraft.timesheet_entries && currentDraft.timesheet_entries.length > 0 && (
                  <div>
                    <Label className="mb-2 block">Daily Hour Breakdown</Label>
                    <div className="border rounded-lg max-h-48 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Start</TableHead>
                            <TableHead>End</TableHead>
                            <TableHead>Break</TableHead>
                            <TableHead>Hours</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {currentDraft.timesheet_entries.map((entry, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{entry.date}</TableCell>
                              <TableCell>{entry.start_time}</TableCell>
                              <TableCell>{entry.end_time}</TableCell>
                              <TableCell>{entry.break_minutes} min</TableCell>
                              <TableCell className="font-medium">{entry.hours?.toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Editable Fields */}
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-medium">Hours</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Regular Hours</Label>
                        <Input
                          type="number"
                          step="0.5"
                          value={currentDraft.regular_hours}
                          onChange={(e) => updateDraft({ regular_hours: parseFloat(e.target.value) || 0 })}
                          data-testid="regular-hours-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Overtime Hours</Label>
                        <Input
                          type="number"
                          step="0.5"
                          value={currentDraft.overtime_hours}
                          onChange={(e) => updateDraft({ overtime_hours: parseFloat(e.target.value) || 0 })}
                          data-testid="overtime-hours-input"
                        />
                      </div>
                    </div>
                    {currentDraft.hours_adjusted && (
                      <p className="text-sm text-amber-600">
                        <Edit2 className="h-4 w-4 inline mr-1" />
                        Hours have been adjusted from imported value
                      </p>
                    )}
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-medium">Rates</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Hourly Rate ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={currentDraft.hourly_rate}
                          onChange={(e) => updateDraft({ hourly_rate: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>OT Rate ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={currentDraft.overtime_rate}
                          onChange={(e) => updateDraft({ overtime_rate: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Additional Earnings */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Bonus ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={currentDraft.bonus || 0}
                      onChange={(e) => updateDraft({ bonus: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Vacation Pay ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={currentDraft.vacation_pay || 0}
                      onChange={(e) => updateDraft({ vacation_pay: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Other Earnings ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={currentDraft.other_earnings || 0}
                      onChange={(e) => updateDraft({ other_earnings: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                </div>

                {/* Adjustment Notes */}
                <div className="space-y-2">
                  <Label>Adjustment Notes (Optional)</Label>
                  <Input
                    value={currentDraft.adjustment_notes || ''}
                    onChange={(e) => updateDraft({ adjustment_notes: e.target.value })}
                    placeholder="Document any adjustments made..."
                  />
                </div>

                <div className="flex gap-3">
                  <Button variant="outline" onClick={resetWorkflow}>
                    Cancel
                  </Button>
                  <Button 
                    className="bg-teal-600 hover:bg-teal-700"
                    onClick={calculatePayroll}
                    data-testid="calculate-btn"
                  >
                    <Calculator className="h-4 w-4 mr-2" />
                    Calculate Deductions
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 4: Complete */}
          {workflowStep === 4 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-emerald-600">Paystub Created Successfully!</CardTitle>
                <CardDescription>The paystub has been created and is ready for finalization</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-6 bg-emerald-50 border border-emerald-200 rounded-lg text-center">
                  <Check className="h-16 w-16 mx-auto text-emerald-500 mb-4" />
                  <p className="text-lg font-medium text-emerald-800">Payroll processing complete!</p>
                  <p className="text-sm text-emerald-600 mt-2">
                    Go to the Paystubs tab to finalize and generate the PDF.
                  </p>
                </div>
                <Button className="w-full" onClick={() => { resetWorkflow(); setActiveTab('history'); }}>
                  View Paystubs
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Drafts Tab */}
        <TabsContent value="drafts">
          <Card>
            <CardHeader>
              <CardTitle>Payroll Drafts</CardTitle>
              <CardDescription>In-progress payroll calculations</CardDescription>
            </CardHeader>
            <CardContent>
              {drafts.length === 0 ? (
                <div className="text-center py-12">
                  <ClipboardList className="h-12 w-12 mx-auto text-slate-300" />
                  <p className="mt-4 text-slate-500">No payroll drafts</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Pay Period</TableHead>
                      <TableHead>Hours</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {drafts.map((draft) => (
                      <TableRow key={draft.id}>
                        <TableCell className="font-medium">{draft.employee_name}</TableCell>
                        <TableCell>{draft.pay_period_start} to {draft.pay_period_end}</TableCell>
                        <TableCell>
                          {draft.regular_hours?.toFixed(2)} + {draft.overtime_hours?.toFixed(2)} OT
                        </TableCell>
                        <TableCell>{getStatusBadge(draft.status)}</TableCell>
                        <TableCell className="text-right">
                          {draft.status !== 'finalized' && (
                            <Button variant="outline" size="sm" onClick={() => loadDraft(draft)}>
                              <Edit2 className="h-4 w-4 mr-1" />
                              Continue
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Paystubs Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Paystub History</CardTitle>
              <CardDescription>All generated paystubs</CardDescription>
            </CardHeader>
            <CardContent>
              {paystubs.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 mx-auto text-slate-300" />
                  <p className="mt-4 text-slate-500">No paystubs generated yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Pay Period</TableHead>
                      <TableHead>Gross</TableHead>
                      <TableHead>Net Pay</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paystubs.map((stub) => (
                      <TableRow key={stub.id}>
                        <TableCell className="font-medium">{stub.employee_name}</TableCell>
                        <TableCell>{stub.pay_period_start} to {stub.pay_period_end}</TableCell>
                        <TableCell>${stub.gross_earnings?.toFixed(2)}</TableCell>
                        <TableCell className="font-bold text-emerald-600">${stub.net_pay?.toFixed(2)}</TableCell>
                        <TableCell>{getStatusBadge(stub.status)}</TableCell>
                        <TableCell className="text-right space-x-2">
                          {stub.status === 'draft' && (
                            <Button variant="outline" size="sm" onClick={() => finalizePaystub(stub.id)}>
                              <Check className="h-4 w-4 mr-1" />
                              Finalize
                            </Button>
                          )}
                          <Button variant="outline" size="sm" onClick={() => downloadPDF(stub.id)}>
                            <Download className="h-4 w-4 mr-1" />
                            PDF
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Employee Settings Tab */}
        <TabsContent value="employees">
          <Card>
            <CardHeader>
              <CardTitle>Employee Payroll Settings</CardTitle>
              <CardDescription>Configure hourly rates and payroll preferences</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Hourly Rate</TableHead>
                    <TableHead>OT Rate</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employees.map((emp) => (
                    <TableRow key={emp.id}>
                      <TableCell className="font-medium">{emp.name}</TableCell>
                      <TableCell>
                        {emp.hourly_rate > 0 ? `$${emp.hourly_rate.toFixed(2)}/hr` : 
                          <span className="text-amber-600">Not Set</span>}
                      </TableCell>
                      <TableCell>
                        {emp.overtime_rate > 0 ? `$${emp.overtime_rate.toFixed(2)}/hr` : '1.5x'}
                      </TableCell>
                      <TableCell>
                        <Badge className={emp.payroll_active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                          {emp.payroll_active !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" onClick={() => openEmployeeSettings(emp)}>
                          <Settings className="h-4 w-4 mr-1" />
                          Configure
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Calculation Preview Dialog */}
      <Dialog open={showCalculation} onOpenChange={setShowCalculation}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Payroll Calculation Preview</DialogTitle>
            <DialogDescription>Review before generating paystub</DialogDescription>
          </DialogHeader>
          
          {currentDraft?.calculation && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-medium">{currentDraft.employee_name}</p>
                <p className="text-sm text-slate-500">{currentDraft.pay_period_start} to {currentDraft.pay_period_end}</p>
              </div>

              <div>
                <h4 className="font-medium mb-2">Earnings</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Regular ({currentDraft.regular_hours} hrs × ${currentDraft.hourly_rate})</div>
                  <div className="text-right">${currentDraft.calculation.regular_earnings?.toFixed(2)}</div>
                  {currentDraft.overtime_hours > 0 && (
                    <>
                      <div>Overtime ({currentDraft.overtime_hours} hrs × ${currentDraft.overtime_rate})</div>
                      <div className="text-right">${currentDraft.calculation.overtime_earnings?.toFixed(2)}</div>
                    </>
                  )}
                  {currentDraft.bonus > 0 && (<><div>Bonus</div><div className="text-right">${currentDraft.bonus.toFixed(2)}</div></>)}
                  {currentDraft.vacation_pay > 0 && (<><div>Vacation Pay</div><div className="text-right">${currentDraft.vacation_pay.toFixed(2)}</div></>)}
                  <div className="font-bold pt-2 border-t">Gross Earnings</div>
                  <div className="font-bold text-right pt-2 border-t">${currentDraft.calculation.gross_earnings?.toFixed(2)}</div>
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-2">Deductions</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>CPP</div><div className="text-right">-${currentDraft.calculation.cpp?.toFixed(2)}</div>
                  <div>CPP2</div><div className="text-right">-${currentDraft.calculation.cpp2?.toFixed(2)}</div>
                  <div>EI</div><div className="text-right">-${currentDraft.calculation.ei?.toFixed(2)}</div>
                  <div>Federal Tax</div><div className="text-right">-${currentDraft.calculation.federal_tax?.toFixed(2)}</div>
                  <div>Ontario Tax</div><div className="text-right">-${currentDraft.calculation.provincial_tax?.toFixed(2)}</div>
                  <div className="font-bold pt-2 border-t">Total Deductions</div>
                  <div className="font-bold text-right pt-2 border-t text-red-600">-${currentDraft.calculation.total_deductions?.toFixed(2)}</div>
                </div>
              </div>

              <div className="p-4 bg-emerald-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-lg">Net Pay</span>
                  <span className="font-bold text-2xl text-emerald-600">${currentDraft.calculation.net_pay?.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCalculation(false)}>Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700" onClick={generatePaystub} data-testid="generate-paystub-btn">
              <FileText className="h-4 w-4 mr-2" />
              Generate Paystub
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employee Settings Dialog */}
      <Dialog open={showEmployeeSettings} onOpenChange={setShowEmployeeSettings}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Employee Payroll Settings</DialogTitle>
            <DialogDescription>{settingsEmployee?.name}</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hourly Rate ($) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={settingsForm.hourly_rate}
                  onChange={(e) => setSettingsForm({...settingsForm, hourly_rate: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label>Overtime Rate ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={settingsForm.overtime_rate}
                  onChange={(e) => setSettingsForm({...settingsForm, overtime_rate: parseFloat(e.target.value) || 0})}
                  placeholder="1.5x hourly if empty"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Employee Number (Optional)</Label>
              <Input
                value={settingsForm.employee_number}
                onChange={(e) => setSettingsForm({...settingsForm, employee_number: e.target.value})}
                placeholder="For payroll records"
              />
            </div>
            <div className="space-y-2">
              <Label>Vacation Pay %</Label>
              <Input
                type="number"
                step="0.5"
                value={settingsForm.vacation_pay_percent}
                onChange={(e) => setSettingsForm({...settingsForm, vacation_pay_percent: parseFloat(e.target.value) || 4})}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmployeeSettings(false)}>Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700" onClick={saveEmployeeSettings}>
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
